import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type { TypeUnit, TypeUnitStatus, TypeUnitErrorCode, ApiEnvelope } from "@/domain/types";

export function createTypeUnitError(code: TypeUnitErrorCode): Error {
  const err = new Error(code);
  err.name = "TypeUnitError";
  return err;
}

export function isTypeUnitError(err: unknown): err is Error & { message: TypeUnitErrorCode } {
  return err instanceof Error && err.name === "TypeUnitError";
}

function mapError(statusCode: number, responseData?: unknown): TypeUnitErrorCode {
  if (statusCode === 409) {
    const msg = typeof responseData === "object" && responseData !== null
      ? (responseData as Record<string, unknown>).message
      : "";
    if (typeof msg === "string" && msg.includes("abbreviation")) return "DUPLICATE_ABBR";
    return "DUPLICATE_CODE";
  }
  if (statusCode === 400) return "VALIDATION";
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listTypeUnits(): Promise<TypeUnit[]> {
  const response = await warehouseClient.get<ApiEnvelope<TypeUnit[]>>("/type-unit");
  return response.data.data ?? [];
}

export async function createTypeUnit(input: { code: string; name: string; abbreviation: string }): Promise<void> {
  try {
    await warehouseClient.post("/type-unit", input);
  } catch (err) {
    const code = extractStatusCode(err);
    const data = extractResponseData(err);
    throw createTypeUnitError(mapError(code, data));
  }
}

export async function updateTypeUnitStatus(id: string, status: TypeUnitStatus): Promise<void> {
  try {
    await warehouseClient.patch("/type-unit/status", { id, status });
  } catch (err) {
    const code = extractStatusCode(err);
    throw createTypeUnitError(mapError(code));
  }
}

function extractResponseData(err: unknown): unknown {
  if (typeof err === "object" && err !== null && "response" in err) {
    const resp = (err as Record<string, unknown>).response;
    if (typeof resp === "object" && resp !== null && "data" in resp) {
      return (resp as Record<string, unknown>).data;
    }
  }
  return undefined;
}
